/**
 * PaywallVariableTextField - Lightweight text field with paywall variable support
 *
 * A simple MUI TextField that shows a variable menu when "@" is typed.
 * Used for paywall fields that don't need full template support (links, labels).
 *
 * Features:
 * - Type "@" to trigger variable menu
 * - Keyboard navigation in menu
 * - Inserts variable at cursor position
 * - Compatible with existing blur-to-save pattern
 *
 * Use Cases:
 * - Subscription item labels (leftLabelTop, etc.)
 * - Bullet item titles
 * - Auto-renew label
 *
 * @module reactFlow/components/shared/PaywallVariableTextField
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TextField, type TextFieldProps } from '@mui/material';
import { PaywallVariablesMenu, type PaywallVariablesMenuRef } from './PaywallVariablesMenu';
import type { PaywallVariable } from '../../config/paywallVariablesConfig';

/**
 * Props for PaywallVariableTextField
 */
export interface PaywallVariableTextFieldProps
  extends Omit<TextFieldProps, 'value' | 'onChange'> {
  /** Current text value */
  value: string;

  /** Callback when text changes */
  onChange: (value: string) => void;

  /** Whether to show variable menu on @ trigger (default: true) */
  enableVariables?: boolean;
}

/**
 * Virtual element for positioning menu at cursor
 */
interface VirtualElement {
  getBoundingClientRect: () => DOMRect;
}

/**
 * Get caret coordinates in a text input
 */
function getCaretCoordinates(input: HTMLInputElement | HTMLTextAreaElement): { x: number; y: number } {
  // Create a hidden div to measure text
  const div = document.createElement('div');
  const style = window.getComputedStyle(input);

  // Copy relevant styles
  const properties = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'wordSpacing',
    'textIndent',
    'paddingLeft',
    'paddingRight',
    'paddingTop',
    'paddingBottom',
    'borderLeftWidth',
    'borderRightWidth',
    'boxSizing',
  ];

  properties.forEach((prop) => {
    div.style[prop as unknown as number] = style.getPropertyValue(
      prop.replace(/([A-Z])/g, '-$1').toLowerCase()
    );
  });

  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.overflowWrap = 'break-word';

  // Set the content up to the cursor position
  const selectionStart = input.selectionStart || 0;
  const textBeforeCursor = input.value.substring(0, selectionStart);
  div.textContent = textBeforeCursor;

  // Add a span at the cursor position
  const span = document.createElement('span');
  span.textContent = '|';
  div.appendChild(span);

  document.body.appendChild(div);

  const inputRect = input.getBoundingClientRect();
  const spanRect = span.getBoundingClientRect();

  document.body.removeChild(div);

  return {
    x: inputRect.left + spanRect.left - div.getBoundingClientRect().left,
    y: inputRect.top + input.offsetHeight,
  };
}

/**
 * PaywallVariableTextField - Text input with @ variable trigger
 */
export function PaywallVariableTextField({
  value,
  onChange,
  enableVariables = true,
  disabled,
  ...textFieldProps
}: PaywallVariableTextFieldProps): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<VirtualElement | null>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const menuRef = useRef<PaywallVariablesMenuRef>(null);

  // Track if we just inserted a variable (to prevent menu reopening)
  const justInsertedRef = useRef(false);

  // Track blur timeout to prevent race condition with menu click
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurEventRef = useRef<React.FocusEvent<HTMLInputElement | HTMLTextAreaElement> | null>(null);

  // Cleanup blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  // Handle input change
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      const newCursorPosition = event.target.selectionStart || 0;

      onChange(newValue);
      setCursorPosition(newCursorPosition);

      // Check if @ was just typed (and we're not just after an insert)
      if (enableVariables && !disabled && !justInsertedRef.current) {
        const charBeforeCursor = newValue[newCursorPosition - 1];
        const charBeforeAt = newCursorPosition > 1 ? newValue[newCursorPosition - 2] : ' ';

        // Show menu if @ is typed at start or after whitespace
        if (charBeforeCursor === '@' && (!charBeforeAt || /\s/.test(charBeforeAt))) {
          const input = event.target;
          const coords = getCaretCoordinates(input);

          setMenuAnchor({
            getBoundingClientRect: () => ({
              top: coords.y,
              left: coords.x,
              bottom: coords.y,
              right: coords.x,
              width: 0,
              height: 0,
              x: coords.x,
              y: coords.y,
              toJSON: () => ({}),
            }),
          });
          setMenuOpen(true);
        }
      }

      justInsertedRef.current = false;
    },
    [onChange, enableVariables, disabled]
  );

  // Handle key down for menu navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (menuOpen && menuRef.current) {
        const handled = menuRef.current.onKeyDown(event);
        if (handled) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    },
    [menuOpen]
  );

  // Handle variable selection
  const handleSelectVariable = useCallback(
    (variable: PaywallVariable) => {
      if (!inputRef.current) return;

      // Clear any pending blur timeout - we're selecting a variable, not losing focus
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
        blurEventRef.current = null;
      }

      const currentValue = value;

      // Find the @ that triggered the menu (should be right before cursor)
      // and replace it with the full variable
      const beforeAt = currentValue.substring(0, cursorPosition - 1);
      const afterAt = currentValue.substring(cursorPosition);

      const newValue = `${beforeAt}@${variable.name}${afterAt}`;
      const newCursorPosition = beforeAt.length + variable.name.length + 1;

      // Mark that we just inserted to prevent menu from reopening
      justInsertedRef.current = true;

      onChange(newValue);
      setMenuOpen(false);

      // Restore focus and cursor position
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      });
    },
    [value, cursorPosition, onChange]
  );

  // Handle menu close
  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);

    // Delete the @ that triggered the menu
    if (inputRef.current && cursorPosition > 0 && value[cursorPosition - 1] === '@') {
      const beforeAt = value.substring(0, cursorPosition - 1);
      const afterAt = value.substring(cursorPosition);
      onChange(beforeAt + afterAt);

      // Restore cursor position
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
        }
      });
    }
  }, [value, cursorPosition, onChange]);

  // Handle blur with delay when menu is open to allow click to complete
  // This fixes the race condition: mousedown → blur → click
  // Without this fix, blur triggers parent re-render before click event fires
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (menuOpen) {
        // Store the event and delay the blur to allow menu click to complete
        blurEventRef.current = event;
        blurTimeoutRef.current = setTimeout(() => {
          // Only call original onBlur if we didn't select a variable
          // (handleSelectVariable clears the timeout)
          if (blurEventRef.current && textFieldProps.onBlur) {
            textFieldProps.onBlur(blurEventRef.current);
          }
          blurEventRef.current = null;
          blurTimeoutRef.current = null;
        }, 200); // Delay to allow click event to complete
      } else {
        // Menu not open - call onBlur immediately
        textFieldProps.onBlur?.(event);
      }
    },
    [menuOpen, textFieldProps]
  );

  return (
    <>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        inputRef={inputRef}
      />

      {enableVariables && (
        <PaywallVariablesMenu
          ref={menuRef}
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={handleMenuClose}
          onSelect={handleSelectVariable}
        />
      )}
    </>
  );
}

export default PaywallVariableTextField;
